import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PotentialLocation, AlgoliaLocation, LocationType } from '@/types/firestore';
import { RootState } from '../index'; // Corrected import path

// --- Session Storage Keys ---
const SESSION_STORAGE_MAP_BOUNDS_KEY = 'sodaTrackerMapBounds';
const SESSION_STORAGE_MAP_ZOOM_KEY = 'sodaTrackerMapZoom';

// --- Helper functions for session storage ---
const loadMapBoundsFromSession = (): google.maps.LatLngBoundsLiteral | null => {
  const storedBounds = sessionStorage.getItem(SESSION_STORAGE_MAP_BOUNDS_KEY);
  if (storedBounds) {
    try {
      return JSON.parse(storedBounds) as google.maps.LatLngBoundsLiteral;
    } catch (e) {
      console.error("Error parsing map bounds from session storage:", e);
      return null;
    }
  }
  return null;
};

const loadMapZoomFromSession = (): number | null => {
  const storedZoom = sessionStorage.getItem(SESSION_STORAGE_MAP_ZOOM_KEY);
  if (storedZoom) {
    try {
      const zoom = parseInt(storedZoom, 10);
      return isNaN(zoom) ? null : zoom;
    } catch (e) {
      console.error("Error parsing map zoom from session storage:", e);
      return null;
    }
  }
  return null;
};

const saveMapStateToSession = (bounds: google.maps.LatLngBoundsLiteral | null, zoom: number): void => {
  if (bounds) {
    sessionStorage.setItem(SESSION_STORAGE_MAP_BOUNDS_KEY, JSON.stringify(bounds));
  } else {
    sessionStorage.removeItem(SESSION_STORAGE_MAP_BOUNDS_KEY);
  }
  sessionStorage.setItem(SESSION_STORAGE_MAP_ZOOM_KEY, zoom.toString());
};

// --- Type Definitions ---
type BrandSizeType = 'all' | 'craft' | 'mass' | 'unknown';

interface DateRangeState {
  start: string | null; // Store as ISO strings or null
  end: string | null;
}

// --- State Interface ---
interface MapUiState {
  activeInfoWindowId: string | null;
  potentialLocations: PotentialLocation[];
  isFiltersCollapsed: boolean;
  // Add other UI states if they were in Zustand, e.g., mapBounds
  // mapBounds: google.maps.LatLngBoundsLiteral | null;

  // Filters previously in Zustand's extendedFilters
  query: string;
  brandSize: BrandSizeType;
  selectedBrands: string[];
  selectedFlavors: string[];
  selectedAttributes: string[];
  // Basic filters also moved here for unified management
  locationTypes: LocationType[] | null;
  visited: boolean | null; // Use null for 'any'
  minRating: number | null;
  maxRating: number | null;
  // ---
  dateRange: DateRangeState;
  isExpanded: boolean; // For the filter box itself

  // State for filter popovers/inputs
  brandSearchOpen: boolean;
  flavorPickerOpen: boolean;
  attributePickerOpen: boolean;
  locationPickerOpen: boolean;
  brandSearchQuery: string;

  // Map state
  mapBounds: google.maps.LatLngBoundsLiteral | null;
  zoom: number;

  // Algolia search results
  algoliaSearchResults: AlgoliaLocation[] | null;
  searchModeActive: boolean;
  mapSelectionTriggeredByUrl: boolean; // New state for URL-triggered selection
  
  // Sidebar state
  activeAccordionItem: string | undefined; // Add this for tracking accordion state
}

// --- Initial State ---
const initialState: MapUiState = {
  activeInfoWindowId: null,
  potentialLocations: [],
  isFiltersCollapsed: false, // Start expanded as per old logic
  // mapBounds: null,

  // Filters
  query: '',
  brandSize: 'all',
  selectedBrands: [],
  selectedFlavors: [],
  selectedAttributes: [],
  locationTypes: null,
  visited: null,
  minRating: null,
  maxRating: null,
  dateRange: { start: null, end: null },
  isExpanded: true, // Default filter box to expanded

  // Component state
  brandSearchOpen: false,
  flavorPickerOpen: false,
  attributePickerOpen: false,
  locationPickerOpen: false,
  brandSearchQuery: '',

  // Map state
  mapBounds: loadMapBoundsFromSession(), // Load from session storage
  zoom: loadMapZoomFromSession() ?? 10, // Load from session storage or default

  // Algolia search results
  algoliaSearchResults: null,
  searchModeActive: false,
  mapSelectionTriggeredByUrl: false, // Initialize to false
  
  // Sidebar state
  activeAccordionItem: "filters", // Default to filters being open
};

// --- Slice Definition ---
const mapUiSlice = createSlice({
  name: 'mapUi',
  initialState,
  reducers: {
    setActiveInfoWindowId: (state, action: PayloadAction<string | null>) => {
      state.activeInfoWindowId = action.payload;
    },
    addPotentialLocation: (state, action: PayloadAction<{ location: PotentialLocation; activate: boolean }>) => {
      const { location, activate } = action.payload;
      const index = state.potentialLocations.findIndex(p => p.id === location.id);
      if (index === -1) {
        state.potentialLocations.push(location);
      } else {
        state.potentialLocations[index] = location; // Update if already exists
      }
      // Set as active only if requested AND location.id exists
      if (activate && location.id) { // Check location.id here
        state.activeInfoWindowId = location.id;
      } else if (activate) {
        // Handle case where activate is true but id is missing (optional: clear active ID?)
        // state.activeInfoWindowId = null; 
        console.warn('Attempted to activate potential location without a valid ID:', location);
      }
    },
    removePotentialLocation: (state, action: PayloadAction<string>) => {
      state.potentialLocations = state.potentialLocations.filter(p => p.id !== action.payload);
      // If the removed one was active, deactivate
      if (state.activeInfoWindowId === action.payload) {
        state.activeInfoWindowId = null;
      }
    },
    clearPotentialLocations: (state) => {
      state.potentialLocations = [];
      state.activeInfoWindowId = null; // Also clear active ID if it was potential
    },
    toggleFiltersCollapsed: (state) => {
      state.isFiltersCollapsed = !state.isFiltersCollapsed;
    },
    // Add reducer to update potential location after save
    updatePotentialLocationId: (state, action: PayloadAction<{ potentialId: string; newLocationId: string }>) => {
        const { potentialId, newLocationId } = action.payload;
        const index = state.potentialLocations.findIndex(p => p.id === potentialId);
        if (index !== -1) {
            // Remove the potential location, assuming it's now a real location
            state.potentialLocations.splice(index, 1);
            // If the potential one was active, switch active ID to the new real one
            if (state.activeInfoWindowId === potentialId) {
                state.activeInfoWindowId = newLocationId;
            }
        } else {
            // If the potential ID wasn't found, but the active ID matches, clear the active ID
            // This handles cases where the potential location might have been cleared before update ID runs
            if (state.activeInfoWindowId === potentialId) {
                 state.activeInfoWindowId = null;
            }
        }
    },
    // Add reducer to update potential location data (e.g., from InfoWindow input)
    updatePotentialLocation: (state, action: PayloadAction<PotentialLocation>) => {
      const updatedLocation = action.payload;
      const index = state.potentialLocations.findIndex(p => p.id === updatedLocation.id);
      if (index !== -1) {
        state.potentialLocations[index] = updatedLocation;
      }
      // Optionally, if the updated one becomes active (e.g., user clicks save?)
      // state.activeInfoWindowId = updatedLocation.id; 
    },
    // Add other reducers if needed, e.g., setMapBounds
    // setMapBounds: (state, action: PayloadAction<google.maps.LatLngBoundsLiteral | null>) => {
    //   state.mapBounds = action.payload;
    // },

    // --- New Filter Reducers ---
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setBrandSize: (state, action: PayloadAction<BrandSizeType>) => {
        state.brandSize = action.payload;
    },
    setSelectedBrands: (state, action: PayloadAction<string[]>) => {
        state.selectedBrands = action.payload;
    },
    setSelectedFlavors: (state, action: PayloadAction<string[]>) => {
        state.selectedFlavors = action.payload;
    },
    setSelectedAttributes: (state, action: PayloadAction<string[]>) => {
        state.selectedAttributes = action.payload;
    },
    setLocationTypes: (state, action: PayloadAction<LocationType[] | null>) => {
        state.locationTypes = action.payload;
    },
    setVisited: (state, action: PayloadAction<boolean | null>) => {
        state.visited = action.payload;
    },
    setMinRating: (state, action: PayloadAction<number | null>) => {
        state.minRating = action.payload;
    },
    setMaxRating: (state, action: PayloadAction<number | null>) => {
        state.maxRating = action.payload;
    },
    setDateRange: (state, action: PayloadAction<DateRangeState>) => {
        state.dateRange = action.payload;
    },
    toggleIsExpanded: (state) => { // For the filter box UI
        state.isExpanded = !state.isExpanded;
    },
    // Popover/Input state setters
    setBrandSearchOpen: (state, action: PayloadAction<boolean>) => {
        state.brandSearchOpen = action.payload;
    },
    setFlavorPickerOpen: (state, action: PayloadAction<boolean>) => {
        state.flavorPickerOpen = action.payload;
    },
    setAttributePickerOpen: (state, action: PayloadAction<boolean>) => {
        state.attributePickerOpen = action.payload;
    },
    setLocationPickerOpen: (state, action: PayloadAction<boolean>) => {
        state.locationPickerOpen = action.payload;
    },
    setBrandSearchQuery: (state, action: PayloadAction<string>) => {
        state.brandSearchQuery = action.payload;
    },
    // Map Bounds
    setMapBounds: (state, action: PayloadAction<google.maps.LatLngBoundsLiteral | null>) => {
        state.mapBounds = action.payload;
        saveMapStateToSession(state.mapBounds, state.zoom);
    },
    setZoom: (state, action: PayloadAction<number>) => {
        state.zoom = action.payload;
        saveMapStateToSession(state.mapBounds, state.zoom);
    },
    // Algolia search results reducers
    setAlgoliaSearchResults: (state, action: PayloadAction<AlgoliaLocation[] | null>) => {
      state.algoliaSearchResults = action.payload;
    },
    setSearchModeActive: (state, action: PayloadAction<boolean>) => {
      state.searchModeActive = action.payload;
    },
    setMapSelectionTriggeredByUrl: (state, action: PayloadAction<boolean>) => { // New reducer
      state.mapSelectionTriggeredByUrl = action.payload;
    },

    // Add reducer for accordion state
    setActiveAccordionItem: (state, action: PayloadAction<string | undefined>) => {
      state.activeAccordionItem = action.payload;
    },

    // Reset all filters
    resetAllFilters: (state) => {
        state.query = initialState.query;
        state.brandSize = initialState.brandSize;
        state.selectedBrands = initialState.selectedBrands;
        state.selectedFlavors = initialState.selectedFlavors;
        state.selectedAttributes = initialState.selectedAttributes;
        state.locationTypes = initialState.locationTypes;
        state.visited = initialState.visited;
        state.minRating = initialState.minRating;
        state.maxRating = initialState.maxRating;
        state.dateRange = initialState.dateRange;
        state.isExpanded = initialState.isExpanded;
        state.brandSearchOpen = initialState.brandSearchOpen;
        state.flavorPickerOpen = initialState.flavorPickerOpen;
        state.attributePickerOpen = initialState.attributePickerOpen;
        state.locationPickerOpen = initialState.locationPickerOpen;
        state.brandSearchQuery = initialState.brandSearchQuery;
        // Reset Algolia search results and mode
        state.algoliaSearchResults = null;
        state.searchModeActive = false;
        state.mapSelectionTriggeredByUrl = false; // Reset mapSelectionTriggeredByUrl
        state.activeAccordionItem = initialState.activeAccordionItem; // Reset accordion state
    },
  },
});

// --- Export Actions ---
export const {
  setActiveInfoWindowId,
  addPotentialLocation,
  removePotentialLocation,
  clearPotentialLocations,
  toggleFiltersCollapsed,
  updatePotentialLocationId,
  updatePotentialLocation,
  // setMapBounds,
  // Filters
  setQuery,
  setBrandSize,
  setSelectedBrands,
  setSelectedFlavors,
  setSelectedAttributes,
  setLocationTypes,
  setVisited,
  setMinRating,
  setMaxRating,
  setDateRange,
  toggleIsExpanded,
  setBrandSearchOpen,
  setFlavorPickerOpen,
  setAttributePickerOpen,
  setLocationPickerOpen,
  setBrandSearchQuery,
  setMapBounds,
  setZoom,
  resetAllFilters,
  // Algolia actions
  setAlgoliaSearchResults,
  setSearchModeActive,
  setMapSelectionTriggeredByUrl, // Export new action
  // Accordion action
  setActiveAccordionItem,
} = mapUiSlice.actions;

// --- Export Selectors ---
// Added explicit return types again
export const selectActiveInfoWindowId = (state: RootState): string | null => state.mapUi.activeInfoWindowId;
export const selectPotentialLocations = (state: RootState): PotentialLocation[] => state.mapUi.potentialLocations;
export const selectIsFiltersCollapsed = (state: RootState): boolean => state.mapUi.isFiltersCollapsed;
// export const selectMapBounds = (state: RootState): google.maps.LatLngBoundsLiteral | null => state.mapUi.mapBounds;

// Filter selectors
export const selectMapQuery = (state: RootState): string => state.mapUi.query;
export const selectMapBrandSize = (state: RootState): BrandSizeType => state.mapUi.brandSize;
export const selectMapSelectedBrands = (state: RootState): string[] => state.mapUi.selectedBrands;
export const selectMapSelectedFlavors = (state: RootState): string[] => state.mapUi.selectedFlavors;
export const selectMapSelectedAttributes = (state: RootState): string[] => state.mapUi.selectedAttributes;
export const selectMapLocationTypes = (state: RootState): LocationType[] | null => state.mapUi.locationTypes;
export const selectMapVisited = (state: RootState): boolean | null => state.mapUi.visited;
export const selectMapMinRating = (state: RootState): number | null => state.mapUi.minRating;
export const selectMapMaxRating = (state: RootState): number | null => state.mapUi.maxRating;
export const selectMapDateRange = (state: RootState): DateRangeState => state.mapUi.dateRange;
export const selectMapIsExpanded = (state: RootState): boolean => state.mapUi.isExpanded; // Filter box expand state

// Component state selectors
export const selectMapBrandSearchOpen = (state: RootState): boolean => state.mapUi.brandSearchOpen;
export const selectMapFlavorPickerOpen = (state: RootState): boolean => state.mapUi.flavorPickerOpen;
export const selectMapAttributePickerOpen = (state: RootState): boolean => state.mapUi.attributePickerOpen;
export const selectMapLocationPickerOpen = (state: RootState): boolean => state.mapUi.locationPickerOpen;
export const selectMapBrandSearchQuery = (state: RootState): string => state.mapUi.brandSearchQuery;

// Map state selector
export const selectMapBounds = (state: RootState): google.maps.LatLngBoundsLiteral | null => state.mapUi.mapBounds;
export const selectMapZoom = (state: RootState): number => state.mapUi.zoom;

// Algolia selectors
export const selectAlgoliaSearchResults = (state: RootState): AlgoliaLocation[] | null => state.mapUi.algoliaSearchResults;
export const selectSearchModeActive = (state: RootState): boolean => state.mapUi.searchModeActive;
export const selectMapSelectionTriggeredByUrl = (state: RootState): boolean => state.mapUi.mapSelectionTriggeredByUrl; // New selector

// Accordion selector
export const selectActiveAccordionItem = (state: RootState): string | undefined => state.mapUi.activeAccordionItem;

// --- Export Reducer ---
export default mapUiSlice.reducer;

// --- Type Augmentation for PotentialLocation ---
// Ensure PotentialLocation includes googlePlaceId if needed by map logic
// This should ideally be in your main types file (e.g., src/types/firestore.ts)
// but added here as a reminder based on previous errors.
/*
declare module '@/types/firestore' {
  interface PotentialLocation {
    googlePlaceId?: string;
    createdAt?: Timestamp;
  }
}
*/ 